import { FC } from 'react';
import { Button } from 'antd';
import { TbDownload, TbFolderSymlink } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { CloudAttachment } from '@/services';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import DropdownButtonSelect, {
    DropdownButtonOption,
} from '@/shared/components/dropdowns/DropdownButton/DropdownButtonSelect';
import { DropdownMenuLabel } from '@/shared/components/dropdowns/DropdownPanel';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import CompactListSelectionToolbar from '@/shared/components/lists/CompactListSelectionToolbar';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';
import { WorkspaceFolderGlyph } from '@/shared/components/workspace-resources';

interface AttachmentBulkActionsProps {
    canMove: boolean;
    folders: CloudAttachment[];
    loading: boolean;
    onClear: () => void;
    onDownload: () => void;
    onMove: (destinationFolderId?: string) => void;
    onSelectAll: () => void;
    selectedCount: number;
    visibleCount: number;
}

const AttachmentBulkActions: FC<AttachmentBulkActionsProps> = ({
    canMove,
    folders,
    loading,
    onClear,
    onDownload,
    onMove,
    onSelectAll,
    selectedCount,
    visibleCount,
}) => {
    const { token } = useAntDesignTheme();
    const folderOptions: Array<DropdownButtonOption<string>> = [
        {
            icon: <WorkspaceFolderGlyph />,
            label: 'Attachment Root',
            value: 'attachment-root',
        },
        ...folders
            .slice()
            .sort((left, right) =>
                (left.relativePath ?? left.name).localeCompare(right.relativePath ?? right.name),
            )
            .map((folder, index) => ({
                dividerBefore: index === 0,
                icon: <WorkspaceFolderGlyph />,
                label: folder.relativePath ?? folder.name,
                value: folder.id,
            })),
    ];

    return (
        <CompactListSelectionToolbar
            disabled={loading}
            itemLabel='Attachment'
            loading={loading}
            selectedCount={selectedCount}
            visibleCount={visibleCount}
            position='static'
            onClear={onClear}
            onSelectAll={onSelectAll}
        >
            <SmallTooltip title='Download Selected' padding='0.5rem 0'>
                <Button
                    aria-label='Download selected attachments'
                    disabled={loading}
                    icon={<TbDownload style={{ color: token.colorTextTertiary }} />}
                    loading={loading}
                    onClick={onDownload}
                />
            </SmallTooltip>
            <DropdownButtonSelect
                disabled={loading || !canMove}
                footer={<LearnMoreFooter topic='attachment folder organization' variant='dropdown' />}
                header={<DropdownMenuLabel icon={<TbFolderSymlink size='0.875rem' />}>Move to Folder</DropdownMenuLabel>}
                matchTriggerWidth={false}
                maxHeight='24rem'
                menuMinWidth='18rem'
                options={folderOptions}
                placement='topLeft'
                onChange={(folderId) => {
                    if (folderId) {
                        onMove(folderId === 'attachment-root' ? undefined : folderId);
                    }
                }}
            >
                <DropdownButton
                    aria-label='Move selected attachments to folder'
                    disabled={loading || !canMove}
                    icon={<TbFolderSymlink style={{ color: token.colorTextTertiary }} />}
                >
                    Move to Folder
                </DropdownButton>
            </DropdownButtonSelect>
        </CompactListSelectionToolbar>
    );
};

export default AttachmentBulkActions;
